import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Printer, Save, Upload, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, inputCls } from "@/components/kit";
import { TableEditor } from "@/components/TableEditor";
import { api, errMsg } from "@/lib/api";

const guess = (label, tables) => {
  const n = (label.match(/\d+/) || [])[0];
  if (/spos/i.test(label)) return tables.find((t) => t.sposi)?.nome || "";
  return n ? tables.find((t) => t.nome === `Tavolo ${n}`)?.nome || "" : "";
};

function ListImport({ c, tables, onDone }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const read = async (files) => {
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const r = (await api.post(`/contracts/${c.id}/seating/read`, fd)).data;
      setRes(r.gruppi.map((g) => ({ ...g, table: guess(g.tavolo, tables) })));
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const apply = async (replace) => {
    try {
      const r = await api.post(`/contracts/${c.id}/seating/apply`, { gruppi: res, replace });
      toast.success(`${r.data.imported} nominativi nella lista ospiti`);
      setRes(null);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Lista degli invitati" testId="seat-import">
      <p className="text-sm text-[#47606B] -mt-2">Foto della lista scritta a penna, PDF, Excel o Word: l'IA legge i nomi e, se la lista è divisa per tavoli, li abbina ai tavoli della mappa.</p>
      <label className={`inline-flex items-center gap-2 rounded-full bg-[#0B6E8F] text-white px-5 py-2.5 text-sm cursor-pointer ${busy ? "opacity-60" : ""}`} data-testid="seat-upload">{busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {busy ? "Sto leggendo la lista…" : "Carica la lista"}<input type="file" multiple accept="image/*,application/pdf,.xlsx,.docx" className="hidden" disabled={busy} onChange={(e) => e.target.files.length && read(e.target.files)} /></label>
      {res && (
        <div className="space-y-3" data-testid="seat-preview">
          {res.map((g, i) => (
            <div key={i} className="rounded-lg border border-[#D4E6EC] p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-sm"><b>{g.tavolo || "Senza tavolo"}</b><span className="text-[#7B929C]">{g.ospiti.length} persone →</span>
                <select className={`${inputCls} w-auto`} value={g.table} onChange={(e) => setRes(res.map((x, j) => (j === i ? { ...x, table: e.target.value } : x)))} data-testid={`seat-map-${i}`}><option value="">Da assegnare</option>{tables.map((t) => <option key={t.nome}>{t.nome}</option>)}</select>
                {g.table && tables.find((t) => t.nome === g.table)?.posti < g.ospiti.length && <span className="text-xs text-[#991B1B]">posti insufficienti</span>}
              </div>
              <div className="text-xs text-[#47606B]">{g.ospiti.map((o) => o.nome + (o.tipo === "bambino" ? " (b)" : "") + (o.allergie ? ` ⚠${o.allergie}` : "")).join(", ")}</div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2"><Button className="rounded-full" onClick={() => apply(false)} data-testid="seat-apply">Aggiungi alla lista ospiti</Button><Button variant="outline" className="rounded-full" onClick={() => apply(true)} data-testid="seat-replace">Sostituisci la lista attuale</Button></div>
        </div>
      )}
    </Panel>
  );
}

function EventMap({ c, plans, s, onSaved }) {
  const nav = useNavigate();
  const sede = plans.find((p) => p.sede === c.data?.sede)?.id || "";
  const [pid, setPid] = useState(s.plan?.id || sede);
  const [layout, setLayout] = useState(s.plan ? s.plan.tables.filter((t) => s.table_ids.includes(t.id)) : null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const plan = plans.find((p) => p.id === pid);
  const change = (fn) => { setLayout(fn); setDirty(true); };
  const auto = async () => {
    if (layout?.length && !window.confirm("Ricreare la mappa dell'evento? Le modifiche fatte a mano su questa mappa si perdono.")) return;
    setBusy(true);
    try {
      const r = (await api.post(`/contracts/${c.id}/seating/auto`, { plan_id: pid })).data;
      setLayout(r.layout);
      setDirty(true);
      setMsg(r.message);
      (r.short ? toast.warning : toast.success)(r.message);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const save = async () => {
    try {
      onSaved((await api.put(`/contracts/${c.id}/seating`, { plan_id: pid, layout })).data);
      setDirty(false);
      toast.success("Mappa dell'evento salvata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const guests = (c.guests || []).length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select className={`${inputCls} w-auto`} value={pid} onChange={(e) => { setPid(e.target.value); setLayout(null); setDirty(false); setMsg(""); }} data-testid="seat-plan">{!pid && <option value="">Scegli la sala…</option>}{plans.map((p) => <option key={p.id} value={p.id}>{p.nome} • {p.sede} • {p.tables.length} tavoli max</option>)}</select>
        {plan && <Button className="rounded-full" disabled={busy} onClick={auto} data-testid="seat-auto">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Wand2 size={15} className="mr-1" />}{layout ? "Ricrea in automatico" : "Crea la mappa per questo evento"}</Button>}
      </div>
      {plan && !layout && <p className="text-sm text-[#47606B]" data-testid="seat-auto-hint">L'app prende dalla disposizione massima della sala solo i tavoli che servono per {guests ? `i ${guests} ospiti della lista` : "gli ospiti indicati nel contratto"}, i più vicini {c.data?.evento_tipo === "Matrimonio" ? "al tavolo degli sposi (aggiunto in automatico)" : "al centro della sala"}. Poi puoi spostarli, aggiungerli o toglierli.</p>}
      {plan && layout && (
        <TableEditor plan={plan} tables={layout} onChange={change} testId="seat-map"
          side={msg && <p className="text-sm rounded-lg bg-[#EAF5F7] p-3" data-testid="seat-auto-msg">{msg}</p>}
          footer={
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EAF5F7]">
              <Button className="rounded-full" disabled={!dirty} onClick={save} data-testid="seat-save"><Save size={15} className="mr-1" /> Salva mappa evento</Button>
              <Button variant="outline" className="rounded-full" disabled={dirty} onClick={() => nav(`/sale/${pid}/stampa?contratto=${c.id}`)} data-testid="seat-print"><Printer size={15} className="mr-1" /> Stampa per gli sposi</Button>
            </div>
          } />
      )}
      {plan && layout && <p className="text-xs text-[#7B929C]">Le modifiche valgono solo per questo evento: la disposizione massima in «Sale» non cambia.</p>}
    </div>
  );
}

export function SeatingPanel({ c, reload }) {
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get(`/contracts/${c.id}/seating`).then((r) => setS(r.data)), [c.id]);
  useEffect(() => {
    load();
    api.get("/floorplans").then((r) => setPlans(r.data));
  }, [load]);
  if (!plans || !s) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const link = async () => (await api.post(`/contracts/${c.id}/hostess-link`)).data.token;
  const shareUrl = s.hostess_token ? `${window.location.origin}/hostess/${s.hostess_token}` : "";
  const evTables = (c.tables || []).filter((t) => t.plan_table_id);
  return (
    <div className="space-y-6">
      <Panel title="Mappa sala dell'evento" testId="seating-panel" right={<div className="flex flex-wrap gap-2"><Button variant="outline" className="rounded-full" onClick={() => nav(`/servizio/${c.id}`)} data-testid="open-live">Servizio sala-cucina</Button><Button className="rounded-full" onClick={() => nav(`/sala/${c.id}`)} data-testid="open-hostess"><MapPin size={15} className="mr-2" /> Modalità hostess</Button></div>}>
        {!plans.length ? <p className="text-sm">Carica prima una planimetria in <a href="/sale" className="text-[#0B6E8F] underline">Sale</a>.</p>
          : <EventMap c={c} plans={plans} s={s} onSaved={(d) => { setS({ ...s, ...d }); reload(); }} />}
      </Panel>
      <ListImport c={c} tables={evTables.map((t) => ({ nome: t.nome, posti: t.posti }))} onDone={() => { reload(); load(); }} />
      <Panel title="Link per hostess esterne" testId="hostess-link-panel">
        <p className="text-sm text-[#47606B] -mt-2">Senza accesso all'app: funziona solo il giorno dell'evento e mostra soltanto ricerca nomi e mappa.</p>
        {shareUrl ? <div className="flex flex-wrap gap-2 items-center"><input readOnly value={shareUrl} className="flex-1 min-w-0 text-xs rounded-md border border-[#D4E6EC] px-3 py-2 bg-[#F5FAFB]" data-testid="hostess-url" /><Button size="sm" variant="outline" className="rounded-full" onClick={() => navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copiato"))} data-testid="hostess-copy">Copia</Button><a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`🗺️ Link hostess per ${c.data?.cliente_nome || "l'evento"}: cerca il nome dell'ospite e vedi tavolo e posizione in sala 👉 ${shareUrl}`)}`} target="_blank" rel="noreferrer" className="rounded-full border border-[#25D366] text-[#128C4A] px-4 py-1.5 text-sm" data-testid="hostess-wa">WhatsApp</a></div>
          : <Button size="sm" variant="outline" className="rounded-full" onClick={async () => { try { await link(); load(); } catch (e) { toast.error(errMsg(e)); } }} data-testid="hostess-create">Crea link hostess</Button>}
      </Panel>
    </div>
  );
}
